import {
  useCallback,
  useRef,
  useLayoutEffect,
} from "react"

import { useFeedContext } from "./FeedContext"
import { FeedDayHeader } from "./FeedDayHeader"
import { FeedItem } from "./FeedItem"
import { ongoingSectionId } from "./useSortedFeedItems"
import styles from "./VirtualListItemContent.module.scss"
import { usePolyglot } from "src/contexts"
import { useToday } from "src/hooks"

interface Props {
  index: number;
}

/*
 * this component renders a header and all activities for a specific day
 */
export function VirtualListItemContent(props: Props) {
  const { index } = props
  const polyglot = usePolyglot()
  const today = useToday()

  const ref = useRef<HTMLDivElement>(null)
  const {
    getIndexItem,
    updateIndexSize,
  } = useFeedContext()

  const updateSize = useCallback(
    () => {
      const element = ref.current
      if (element) {
        const { height } = element.getBoundingClientRect()

        updateIndexSize(
          index,
          height,
        )
      }
    },
    [
      ref,
      index,
      updateIndexSize,
    ],
  )

  const item = getIndexItem(index)

  // the height of this element should only change when the activities change
  // if other things (like resizing the window) result in list item height changes
  // then this needs to be updated
  useLayoutEffect(
    () => {
      updateSize()
    },
    [
      updateSize,
      item,
    ],
  )

  if (!item) {
    return null
  }

  return (
    <div
      className={styles.container}
      ref={ref}
    >
      {typeof item === "string" ? (
        item === ongoingSectionId ? (
          <FeedDayHeader
            day={today}
            text={polyglot.t("pages.home.activity_list.ongoing")}
            index={index}
          />
        ) : (
          <FeedDayHeader
            day={item}
            index={index}
          />
        )
      ) : (
        <FeedItem
          activityItem={item}
          key={item.activity.id}
        />
      )}
    </div>
  )
}
